import { default as classnames, default as classNames } from "classnames";
import { useEffect, useRef, useState } from "react";
import SVG from "react-inlinesvg";
import { useHistory } from "react-router-dom";
import { useStableCallback } from "src/hooks/useStableCallback";
import { MenuPopover, MenuPopoverContent, MenuPopoverTrigger } from "src/sudokit/components/MenuPopover";
import { SudoButton } from "src/sudokit/components/SudoButton";
import { useDebouncedCallback } from "use-debounce";
import { MAX_ITEMS_PER_DOCUMENT } from "../../../../../src/shared/constants";
import { EXPAND_STORY_BIBLE_MENU_EVENT } from "../../PersistentChat/mentions/navigateToEntityMention";
import { useAppStore } from "../../../stores/appStore";
import { useCrossAppSignalStore, CrossAppSignal } from "../../../stores/crossAppSignalStore";
import { useProjectStore } from "../../../stores/projectStore";
import { useSeriesStore } from "../../../stores/seriesStore";
import { useStoryBibleStore } from "../../../stores/storyBibleStore";
import CircleIconButton from "../../CircleIconButton";
import IconButton from "../../IconButton";
import { ImportButton } from "../../ImportButton";
import StickyNoteAnchor from "../../StickyTour/StickyNoteAnchor";
import { AnchorIds } from "../../StickyTour/TourTypes";
import TextButton from "../../TextButton";
import { useToast } from "../../ToastWrapper";
import { DeletedDocumentListItem } from "../DeletedDocumentListItem";
import ChapterSelector from "./ChapterSelector";
import { DocumentsListSB } from "./DocumentListSB";
import { SharedChatThemeProvider } from "src/components/SharedChatThemeProvider";
import { EmptyTrashConfirm } from "./EmptyTrashConfirm";
import { StoryBibleMenu } from "./StoryBibleMenu";
import { StyledLeftBarContainer } from "./styles";
import { TrashedItemMenu } from "./TrashedItemMenu";
import { useTrashedItems } from "./useTrashedItems";
import { shouldAutoTitle, shouldProceedAfterDelay } from "./autoTitleGuards";

export default function LeftBar({
  setCollapsed,
  collapsed,
  isSW2, // TODO: Remove this when 2.0 is default
  compact,
  separateCollapseRow,
}) {
  // Project values
  const project = useProjectStore((state) => state.project);
  const contents = useProjectStore((state) => state.contents);
  const deletedDocuments = useProjectStore((state) => state.deletedDocuments);
  const setDocumentId = useProjectStore((state) => state.setDocumentId);
  const setEditingTitle = useProjectStore((state) => state.setEditingTitle);
  const editingTitle = useProjectStore((state) => state.editingTitle);
  const setIsEditing = useProjectStore((state) => state.setIsEditing);
  const documentId = useProjectStore((state) => state.document?.id);
  const creatingDocument = useProjectStore((state) => state.creatingDocument);
  const setCreatingDocument = useProjectStore((state) => state.setCreatingDocument);
  const columnWidths = useProjectStore((state) => state.columnWidths);
  const setTrashedItem = useProjectStore((state) => state.setTrashedItem);
  const setExitTrashView = useProjectStore((state) => state.setExitTrashView);
  const trashedItem = useProjectStore((state) => state.trashedItem);
  const isDeletingDocumentFolder = useProjectStore((state) => state.isDeletingDocumentFolder);
  const setProseGeneratorActive = useStoryBibleStore((state) => state.setProseGeneratorActive);
  const setShowLinkingMenu = useProjectStore((state) => state.setShowLinkingMenu);
  const setIsCreatingNewBlankDocument = useProjectStore((state) => state.setIsCreatingNewBlankDocument);
  // Editing states
  const [newTitle, setNewTitle] = useState("");
  const [editingProjectTitle, setEditingProjectTitle] = useState(false);
  const [newProjectTitle, setNewProjectTitle] = useState(null);
  const [showDeleted, setShowDeleted] = useState(false); // shows the deleted documents
  const [showEmptyTrashConfirm, setShowEmptyTrashConfirm] = useState(false);
  const emptyTrashButtonRef = useRef(null);
  const [showAddNewMenu, setShowAddNewMenu] = useState(false);
  const { trashedCharacters, trashedEntries, trashedChats } = useTrashedItems(showDeleted, project.id, project.seriesId);
  const isStoryBibleActive = useStoryBibleStore((state) => state.isActive);
  const importBlocksLeftBar = !!(project?.importStep && project.importStep !== "CANCELLED");

  useEffect(() => {
    const onExpandStoryBibleMenu = () => {
      setIsStoryBibleCollapsed(false);
    };
    window.addEventListener(EXPAND_STORY_BIBLE_MENU_EVENT, onExpandStoryBibleMenu);
    return () => window.removeEventListener(EXPAND_STORY_BIBLE_MENU_EVENT, onExpandStoryBibleMenu);
  }, []);

  useEffect(() => {
    if (showDeleted) {
      setExitTrashView(() => {
        if (contents?.[0]?.id) {
          setDocumentId(contents[0].id);
        }
        setShowDeleted(false);
        setTrashedItem();
      });
    } else {
      setExitTrashView(() => {});
    }
    return () => setExitTrashView(() => {});
  }, [showDeleted, setExitTrashView, setTrashedItem, contents, setDocumentId]);
  const [isDragging, setIsDragging] = useState(null); // whether user is sorting documents

  const [collapseStoryBibleOnHover, setCollapseStoryBibleOnHover] = useState(true);
  const [isDocumentsListHovered, setIsDocumentsListHovered_] = useState(false);
  const [isStoryBibleCollapsed, setIsStoryBibleCollapsed] = useState(false);
  const setIsDocumentsListHovered = useDebouncedCallback(setIsDocumentsListHovered_, 1000);
  const { setToast } = useToast();
  const history = useHistory();

  const inputRef = useRef(null);
  const listRef = useRef(null);
  const storyBibleMenuRef = useRef(null);

  const isMac = useAppStore((state) => state.isMac);

  const seriesContents = useSeriesStore((state) => state.contents);
  const setSeriesContents = useSeriesStore((state) => state.setContents);

  // when changing documents, determine whether we need to show it in the trash or not
  // If the document is in contents, it takes priority over deletedDocuments
  useEffect(() => {
    const isInContents = contents?.find((doc) => doc.id === documentId);
    if (!isInContents && deletedDocuments?.find((doc) => doc.id === documentId)) {
      setShowDeleted(true);
    } else {
      setShowDeleted(false);
    }
  }, [documentId]);

  useEffect(() => {
    if (showDeleted) {
      if (deletedDocuments?.[0]) {
        setDocumentId(deletedDocuments[0].id);
      }
    }
  }, [showDeleted]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [inputRef.current]);

  useEffect(() => {
    if (editingProjectTitle || editingTitle) {
      setIsEditing(true);
    } else {
      setIsEditing(false);
    }
  }, [editingProjectTitle, editingTitle]);

  // sets the project title automatically to the first automatic doc title
  useEffect(() => {
    const projectTitle = project.generateProjectTitle();

    if (shouldAutoTitle(editingProjectTitle, editingTitle, project.isUntitled(), projectTitle)) {
      // we set an artificial delay to ensure that the project update does not execute until after
      // the document title has been updated on the client (otherwise it overwrites it)
      const timeoutId = setTimeout(() => {
        // Re-check isUntitled — user may have manually renamed during the delay
        if (!shouldProceedAfterDelay(project.isUntitled())) return;

        project.update({
          title: projectTitle,
          hasAutomaticTitle: true,
        });
      }, 2000);

      return () => clearTimeout(timeoutId);
    }
  }, [contents]);

  const checkIfStoryBibleShouldCollapse = () => {
    if (listRef.current && !isStoryBibleCollapsed) {
      // Collapse story bible if it hovers over document lists
      const isOverflowing = listRef.current.scrollHeight > listRef.current.clientHeight;
      setCollapseStoryBibleOnHover(isOverflowing);
    }
  };

  const numberOfTreeNodes = (project.folders?.length || 0) + (contents?.length || 0);

  useEffect(() => {
    checkIfStoryBibleShouldCollapse();
  }, [numberOfTreeNodes]);

  useEffect(() => {
    checkIfStoryBibleShouldCollapse();
  }, [isDocumentsListHovered]);

  useEffect(() => {
    // Wait for animation to finish before checking overflow.
    const timeout = setTimeout(() => {
      checkIfStoryBibleShouldCollapse();
    }, 800);

    return () => {
      clearTimeout(timeout);
    };
  }, [isStoryBibleActive]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("sb")) {
      setIsStoryBibleCollapsed(false);
    }
  }, []);

  useEffect(() => {
    if (!isStoryBibleCollapsed && isDocumentsListHovered && collapseStoryBibleOnHover) {
      setIsStoryBibleCollapsed(true);
    }
  }, [isDocumentsListHovered, collapseStoryBibleOnHover, isStoryBibleCollapsed]);

  const updateTitle = useStableCallback((title) => {
    const value = title ?? newProjectTitle;
    if (value && value !== project.title) {
      project.update({
        title: value,
        hasAutomaticTitle: false,
      });

      // if the project is in a series, we need to optimistically update the contents
      if (project.seriesId) {
        const updatedContents = seriesContents.map((item) => (item.projectId === project.id ? { ...item, title: value } : item));
        setSeriesContents(updatedContents);
      }
    }

    setEditingProjectTitle(false);
    setNewProjectTitle(null);
  });

  // manages state for manual doc title updates
  const updateDocumentTitle = useStableCallback((editingTitle, newTitle) => {
    if (editingTitle && newTitle) {
      project.updateDocument({
        id: editingTitle,
        title: newTitle,
        hasAutomaticTitle: false, // defaults to false anyway, but we define it here to be explicit
      });

      setEditingTitle(null);
    }
  });

  async function addNewDocument() {
    if (!creatingDocument) {
      setCreatingDocument(true);
      setIsCreatingNewBlankDocument(true); // flag for when we are creating a blank doc vs a linked chapter doc
      const result = await project.createDocument({ afterDocumentId: documentId || null });
      setCreatingDocument(false);
      setDocumentId(result.document.id); // go to the newly created doc

      setShowAddNewMenu(false);
      setProseGeneratorActive(false);
      setShowLinkingMenu(false);
    }
    // TODO: add toast, telling user that document creation is in progress
  }

  const newDocDisabled = creatingDocument || contents.length >= MAX_ITEMS_PER_DOCUMENT;
  const totalTrashed =
    (deletedDocuments?.length || 0) + trashedCharacters.length + trashedEntries.length + trashedChats.length;

  return (
    <StyledLeftBarContainer className={classNames("leftbar-component-container", { sw2: isSW2, compact, "separate-collapse-row": separateCollapseRow })}>
      <StickyNoteAnchor anchorId={AnchorIds.LEFT_BAR} />
      {/* Style the cloned draggable list item which is directly on the body */}
      <style>{`
        body > .project-sortable {
          white-space: nowrap;
          text-align: left;
        }

        body > .project-sortable .document-label {
          display: flex;
          align-items: center;
        }

        body > .project-sortable .document-label svg {
          width: 38px;
          height: auto;
          flex-shrink: 0;
        }

        body > .project-sortable .document-label svg path {
          fill: var(--grey-0)
        }

        body > .project-sortable .item-menu {
          display: none;
        }
      `}</style>
      <div className="top-menu">
        <div className="collapse">
          {collapsed ? (
            <CircleIconButton
              onClick={() => {
                if (importBlocksLeftBar) return;
                setCollapsed(false);
              }}
              src="/assets/folders.svg"
              ariaLabel={importBlocksLeftBar ? "Expand Left Bar (unavailable while importing)" : "Expand Left Bar"}
              hoverTipText={importBlocksLeftBar ? "Unavailable while importing" : "Documents"}
            />
          ) : (
            <IconButton
              onClick={() => setCollapsed(true)}
              src="/assets/collapse-sidebar.svg"
              ariaLabel="Collapse Left Bar"
            />
          )}
        </div>
        {/* SW-7957: project title moved to ProjectTopBar */}
      </div>

      {/* NORMAL DOCS */}
      {!showDeleted && (
        <>
          <div className="create-section">
            <MenuPopover
              open={showAddNewMenu}
              onOpenChange={(open) => {
                if (open && newDocDisabled) return;
                setShowAddNewMenu(open);
              }}
            >
              <MenuPopoverTrigger>
                <div
                  role="button"
                  tabIndex={0}
                  aria-label="Create New Document"
                  aria-disabled={newDocDisabled || undefined}
                  className={classnames("add", { disabled: newDocDisabled }, { sw2: isSW2 })}
                  onClick={(e) => {
                    if (newDocDisabled) {
                      e.preventDefault();
                    }
                  }}
                >
                  <SVG
                    src="/assets/add.svg"
                    aria-hidden="true"
                  ></SVG>
                  <p>New</p>
                </div>
              </MenuPopoverTrigger>
              <MenuPopoverContent
                side="bottom"
                align="start"
                sideOffset={8}
                aria-label="Create new item"
                className="max-w-[300px] max-h-[400px] overflow-y-auto"
              >
                <ChapterSelector
                  project={project}
                  setDocumentId={setDocumentId}
                  addNewDocument={addNewDocument}
                  closeMenu={() => setShowAddNewMenu(false)}
                />
              </MenuPopoverContent>
            </MenuPopover>
            <ImportButton
              listRef={listRef}
              project={project}
              setDocumentId={setDocumentId}
              newDocDisabled={newDocDisabled}
            />
          </div>

          <div
            className={classnames("list-container", { "styled-scrollbars": !isMac })}
            ref={listRef}
            onMouseOver={() => {
              setIsDocumentsListHovered.callback(true);
              setIsDocumentsListHovered.flush();
            }}
            onMouseLeave={() => {
              setIsDocumentsListHovered.callback(false);
            }}
          >
            <SharedChatThemeProvider>
              <DocumentsListSB
                contents={contents}
                updateDocumentTitle={updateDocumentTitle}
                newTitle={newTitle}
                setNewTitle={setNewTitle}
                inputRef={inputRef}
                updateTitle={updateTitle} // updated project title, not sure why DocumentListItem needs this, but apparently it does?
              />
            </SharedChatThemeProvider>
          </div>

          {isSW2 && (
            <div className="sw2-container">
              <StoryBibleMenu
                collapse={isStoryBibleCollapsed}
                //collapsed={collapseStoryBibleOnHover}
                parentCollapsed={columnWidths.left.collapsed}
                setIsStoryBibleCollapsed={setIsStoryBibleCollapsed}
                ref={storyBibleMenuRef}
              />
            </div>
          )}
          <div className="bottom-links">
            <BottomLink className="trash">
              <SudoButton
                onClick={() => {
                  if (isDeletingDocumentFolder) {
                    setToast("Moving items to trash. Please wait until the process is complete.");
                  } else if (totalTrashed > 0) {
                    setShowDeleted(true);
                  } else {
                    setToast("This project's trash is empty.");
                  }
                }}
                className={classnames("delete-button", { disabled: totalTrashed === 0 || isDeletingDocumentFolder })}
                title={isDeletingDocumentFolder ? "Folder deletion in progress..." : undefined}
              >
                <SVG
                  width={24}
                  height={24}
                  src="/assets/delete.svg"
                  className="trash"
                />{" "}
                <div className="trash-text">{totalTrashed > 0 ? `Trash\u00A0(${totalTrashed})` : "Trash"}</div>
              </SudoButton>
            </BottomLink>
          </div>
        </>
      )}

      {/* TRASH DOCS */}
      {showDeleted && (
        <>
          <hr />
          <TextButton
            onClick={(e) => {
              e.preventDefault();
              if (contents?.[0]?.id) {
                setDocumentId(contents[0].id);
              } else {
                history.replace({ hash: "" });
              }
              setShowDeleted(false);
              setTrashedItem();
            }}
            className="back use-component-styles"
          >
            &lt;-&nbsp;&nbsp;Back
          </TextButton>
          <hr />
          <div className="list-container trashed">
            <div className="documents-section">
              <h3>Documents</h3>
              <ul>
                {deletedDocuments
                  ?.filter((item) => !contents?.find((doc) => doc.id === item.id))
                  .map((item, idx) => {
                    const title = item.title?.length > 0 ? item.title : "Untitled";
                    const untitled = !item.title?.length;

                    return (
                      <DeletedDocumentListItem
                        key={item.id}
                        project={project}
                        contents={contents}
                        setDocumentId={setDocumentId}
                        documentId={documentId}
                        setEditingTitle={setEditingTitle}
                        editingTitle={editingTitle}
                        title={title}
                        untitled={untitled}
                        newTitle={newTitle}
                        setNewTitle={setNewTitle}
                        isDragging={isDragging}
                        updateTitle={updateTitle}
                        item={item}
                        idx={idx}
                        total={contents.length}
                        trashedItem={trashedItem}
                      />
                    );
                  })}
              </ul>
            </div>
            {trashedCharacters.length > 0 && (
              <>
                <hr />
                <div className="characters-section">
                  <h3>Characters</h3>
                  <ul>
                    {trashedCharacters.map((item, idx) => {
                      const active = trashedItem?.type === "character" && trashedItem.item.id === item.id;
                      return (
                        <li
                          key={item.id}
                          className={classNames("document-list-item trashed", { active })}
                        >
                          <button
                            aria-label={`Select ${item.displayName || "Unnamed Character"}`}
                            onClick={() => setTrashedItem({ type: "character", item })}
                            className="document-label use-component-styles"
                          >
                            <SVG
                              src="/assets/character-icon.svg"
                              className="document-icon"
                              aria-hidden="true"
                            />
                            <pre>{item.displayName || "Unnamed Character"}</pre>
                          </button>
                          <TrashedItemMenu handleRestore={item.restore} />
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </>
            )}
            {trashedEntries.length > 0 && (
              <>
                <hr />
                <div className="worldbuilding-section">
                  <h3>Worldbuilding</h3>
                  <ul>
                    {trashedEntries.map((item, idx) => {
                      console.log("trash item", item, trashedItem);
                      const active = trashedItem?.type === "element" && trashedItem.item.id === item.id;
                      return (
                        <li
                          key={item.id}
                          className={classNames("document-list-item trashed", { active })}
                        >
                          <button
                            aria-label={`Select ${item.displayName || "Unnamed Element"}`}
                            onClick={() => setTrashedItem({ type: "element", item })}
                            className="document-label use-component-styles"
                          >
                            <SVG
                              src="/assets/world.svg"
                              className="document-icon"
                              aria-hidden="true"
                            />
                            <pre>{item.displayName || "Unnamed Element"}</pre>
                          </button>
                          <TrashedItemMenu handleRestore={item.restore} />
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </>
            )}
            {trashedChats.length > 0 && (
              <>
                <hr />
                <div className="chats-section">
                  <h3>Chat</h3>
                  <ul>
                    {trashedChats.map((item) => {
                      const active = trashedItem?.type === "chat" && trashedItem.item.id === item.id;
                      return (
                        <li
                          key={item.id}
                          className={classNames("document-list-item trashed", { active })}
                        >
                          <button
                            type="button"
                            aria-label={`Select deleted chat: ${item.title || "Untitled Chat"}`}
                            onClick={() => setTrashedItem({ type: "chat", item })}
                            className="document-label use-component-styles"
                          >
                            <SVG
                              src="/assets/CommentMagic.svg"
                              className="document-icon"
                              aria-hidden="true"
                            />
                            <pre>{item.title || "Untitled Chat"}</pre>
                          </button>
                          <TrashedItemMenu
                            handleRestore={async () => {
                              await item.restore(() => {
                                // Navigate away from the deleted document before clearing trash view,
                                // otherwise the "This document is in the trash" banner remains visible
                                if (contents?.[0]?.id) {
                                  setDocumentId(contents[0].id);
                                }
                                // Switch back to normal view after restoring
                                setShowDeleted(false);
                                setTrashedItem();
                                // Signal the PersistentChat to switch to the restored chat
                                useCrossAppSignalStore.getState().setSignalActive(CrossAppSignal.CHAT_RESTORED_FROM_TRASH, true, { chatId: item.id });
                              });
                            }}
                          />
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </>
            )}
            <hr />
            <TextButton
              ref={emptyTrashButtonRef}
              onClick={(e) => setShowEmptyTrashConfirm(true)}
              className={`document-label trash empty-trash pb-2 rounded-lg ${showEmptyTrashConfirm ? "pointer-events-none" : ""}`}
            >
              Empty Trash
            </TextButton>

            {showEmptyTrashConfirm && (
              <EmptyTrashConfirm
                setShowEmptyTrashConfirm={setShowEmptyTrashConfirm}
                setShowDeleted={setShowDeleted}
                anchorRef={emptyTrashButtonRef}
              />
            )}
          </div>
        </>
      )}
    </StyledLeftBarContainer>
  );
}

const BottomLink = ({ canvasSticky, width, children, className }) => {
  return (
    <div
      className={classnames("bottom-link", { sticky: canvasSticky, [className]: className })}
      style={{
        width: Math.ceil(width) + "px",
      }}
    >
      {children}
    </div>
  );
};
